import React from 'react'
import { compose, withState } from 'recompose'
import {
  removeItemArrayByIndex,
  updateItemArrayByIndex,
  moveAfter,
  moveBefore,
} from 'helpers/arrayHelpers'
import FormSelect from 'components/forms/bases/FormSelect'
import ColumnWidthSelector from 'components/forms/ColumnWidthSelector'
import FormTextAlign from 'components/forms/FormTextAlign'
import FormSwitch from 'components/forms/bases/FormSwitch'
import { Grid } from 'semantic-ui-react'
import styled from 'styled-components'
import { updateObject } from 'helpers/objectHelpers'
import VerticalAlignSelector from 'components/forms/VerticalAlignSelector'
import AppearanceCheckbox from 'components/forms/AppearanceCheckbox'
import muiThemeable from 'material-ui/styles/muiThemeable'
import { initialColumn } from 'helpers/initialState'

const wrapperStyle = {
  padding: 15,
  marginBottom: 10,
  border: '1px solid #e8e8e8',
  borderRadius: 5,
}

const ColumnEditor = ({
  value,
  value: {
    width = '',
    computer = '',
    tablet = '',
    mobile = '',
    only = '',
    textAlign = '',
    verticalAlign = '',
    stretched = false,
    floated = '',
  },
  onChange,
}) => {
  const handleChange = (val, key) => {
    return onChange(updateObject(value, { [key]: val }))
  }
  return (
    <div>
      <FormSwitch
        label="Stretch"
        value={stretched}
        onChange={(val) => handleChange(val, 'stretched')}
      />
      <ColumnWidthSelector
        label="Default Width"
        value={width}
        onChange={(val) => handleChange(val, 'width')}
      />
      <ColumnWidthSelector
        label="Width in Computer"
        value={computer}
        onChange={(val) => handleChange(val, 'computer')}
      />
      <ColumnWidthSelector
        label="Width in Tablet"
        value={tablet}
        onChange={(val) => handleChange(val, 'tablet')}
      />
      <ColumnWidthSelector
        label="Width in Mobile"
        value={mobile}
        onChange={(val) => handleChange(val, 'mobile')}
      />
      <FormTextAlign
        value={textAlign}
        onChange={(val) => handleChange(val, 'textAlign')}
      />
      <VerticalAlignSelector
        value={verticalAlign}
        onChange={(val) => handleChange(val, 'verticalAlign')}
      />
      <FormSelect
        label="Floated"
        value={floated}
        options={['', 'left', 'right']}
        onChange={(val) => handleChange(val, 'floated')}
      />
      <AppearanceCheckbox
        value={only}
        onChange={(val) => handleChange(val, 'only')}
      />
    </div>
  )
}


const Icon = muiThemeable()(
  styled.i`
    ${({ 
      muiTheme: {
        palette: {
          accent1Color,
        }
      },
      simple,
    }) => ({
      width: '25px',
      height: '25px',
      padding: '5px',
      margin: '3px',
      border: simple ? '0px' : '1px solid #ffffff',
      borderRadius: '50%',
      color: '#ffffff',
      transition: '0.3s',
      fontSize: '12px',
      textAlign: 'center',
      ...!simple && {
        ':hover': {
          color: accent1Color,
          backgroundColor: '#ffffff',
        }
      }
    })}
  `
)

const createBlockCss = ({
  muiTheme: {
    palette: {
      borderColor,
      accent1Color,
    }
  },
  isActive
}) => ({
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  position: 'relative',
  border: `1px solid ${isActive ? accent1Color : borderColor}`,
  borderRadius: '5px',
  width: '100%',
  minHeight: '50px',
  transition: '0.3s',
  ':hover': {
    backgroundColor: accent1Color,
  }
})
const Block = compose(
  withState('displayUtils', 'setDisplayUtils', false),
  muiThemeable()
)(
  styled(
    ({
      className,
      text,
      onEdit,
      isNext,
      onSwapNext,
      isPrevious,
      onSwapPrevious,
      onDelete,
      displayUtils,
      setDisplayUtils,
    }) => {
      return (
        <a
          className={className}
          onMouseEnter={() => setDisplayUtils(true)}
          onMouseLeave={() => setDisplayUtils(false)}
        >
          {!displayUtils && text}
          {displayUtils &&
            <div
              style={{
                float: 'left',
                display: 'block',
                width: '100%',
                height: '100%',
                textAlign: 'center',
                top: 0,
                left: 0,
              }}
            >
              {isPrevious &&
                <Icon
                  onClick={onSwapPrevious}
                  simple className="fa fa-chevron-left"
                />
              }
              <Icon
                onClick={onEdit}
                className="fa fa-pencil"
              />
              <Icon
                onClick={onDelete}
                className="fa fa-trash"
              />
              {isNext &&
                <Icon
                  onClick={onSwapNext}
                  simple className="fa fa-chevron-right"
                />
              }
            </div>
          }
        </a>
      )
    }
  )`
  ${createBlockCss}
`
)

const ColumnsPanel = compose(
  withState('activeIndex', 'setActiveIndex', 0)
)(
  ({
    value: columns = [], // columns
    onChange,
    activeIndex,
    setActiveIndex,
  }) => {
    const activeColumn = columns[activeIndex]
    return (
      <div style={{ padding: 15 }}>
        <Grid style={{ margin: -5 }}>
          {columns.map((value, i) => {
            return (
              <Grid.Column
                key={i}
                style={{ padding: 5 }}
                width={value.defaultSettings.width}
              >
                <Block
                  isNext={i !== columns.length - 1}
                  isPrevious={i !== 0}
                  text={i + 1}
                  isActive={activeIndex === i}
                  onEdit={() => setActiveIndex(i)}
                  onDelete={() => onChange(removeItemArrayByIndex(columns, i))}
                  onSwapNext={() => onChange(moveAfter(columns, i))}
                  onSwapPrevious={() => onChange(moveBefore(columns, i))}
                />
              </Grid.Column>
            )
          })}
          <Grid.Column width={2}>
            <a onClick={() => onChange(columns.concat(initialColumn))}>
              <i className="fa fa-plus" />
            </a>
          </Grid.Column>
        </Grid>
        <p style={{ margin: '10px 0' }}>
          Settings for Column : {activeIndex + 1}
        </p>
        {activeColumn &&
          <div style={wrapperStyle}>
            <ColumnEditor
              value={activeColumn.defaultSettings}
              onChange={(val) => onChange(
                updateItemArrayByIndex(columns, activeIndex, () => ({
                  ...activeColumn,
                  defaultSettings: val
                }))
              )}
            />
          </div>
        }
      </div>
    )
  }
)

ColumnsPanel.propTypes = {}
ColumnsPanel.defaultProps = {}

export default ColumnsPanel



// WEBPACK FOOTER //
// ./src/components/Canvas/builder/components/panel/ColumnsPanel.js